import React from 'react';
import { view, textFormatting, contentEditable, extensions, bowser } from '@yola/ws-sdk';
import { AI_REWRITE_TEXT_TRIGGER_ID } from 'src/js/modules/control-pane/constants/trigger-ids';
import serviceClient from 'yola-editor/src/js/modules/feature-flags/service-client';
import getParentBlockByElement from 'src/js/modules/blocks/helpers/get-parent-block-by-element';
import constants from '../constants';
import isAiRewriteContextMenuVisible from '../verifiers/is-ai-rewrite-context-menu-visible';
import isAiRewriteCollapsedTextAllowed from '../verifiers/is-ai-rewrite-collapsed-text-allowed';
import typedElementsMatches from '../helpers/typed-elements-matches';
import selectAllText from '../helpers/select-all-text';
import {
  showAiRewriteContextMenu,
  hideAiRewriteContextMenu,
} from '../helpers/toggle-ai-rewrite-context-menu';
import { applySelectionWrapper, undoSelectionWrapper } from '../helpers/selection-wrapper';
import { makeTextSelectable, removeTextSelectable } from '../helpers/text-selectable';
import restoreRange from '../helpers/restore-range';
import AiWriterTrigger from '../components/ai-writer-trigger';

const { controlTypes } = extensions;
const { SLUG, AI_WRITER_TRIGGER_WIDTH } = constants.common;

const triggerId = AI_REWRITE_TEXT_TRIGGER_ID;
const withSelectionWrapper = bowser.ios || bowser.iPadOS;
const withTextSelectableAttribute = bowser.safari || bowser.firefox;

const handleStartInteraction = (elementId) => {
  if (!isAiRewriteContextMenuVisible()) {
    if (withTextSelectableAttribute) makeTextSelectable(elementId);
    return;
  }

  if (withSelectionWrapper) {
    selectAllText();
  }
};

const handleTriggerClick = (elementId, { getContext }) => {
  if (isAiRewriteContextMenuVisible()) {
    hideAiRewriteContextMenu();
    restoreRange(textFormatting.accessors.getAdvancedSelection(), elementId);

    return;
  }

  selectAllText();
  showAiRewriteContextMenu({
    elementId,
    triggerId,
    getContext,
    focusedElementInfo: contentEditable.helpers.getFocusedElementInfo(),
    onHideExceptInsideClick: () => {
      const element = view.accessors.getLiveElement(elementId);

      if (withSelectionWrapper) {
        undoSelectionWrapper(element);
        selectAllText();
      }

      if (withTextSelectableAttribute) {
        removeTextSelectable(elementId);
      }

      restoreRange(textFormatting.accessors.getAdvancedSelection(), elementId);
    },
  });

  if (withSelectionWrapper) {
    const element = view.accessors.getLiveElement(elementId);

    applySelectionWrapper();
    element.blur();
    element.setAttribute('contenteditable', 'false');
  } else {
    restoreRange(textFormatting.accessors.getAdvancedSelection(), elementId);
  }
};

const handleUnmount = (elementId) => {
  if (withSelectionWrapper) undoSelectionWrapper(view.accessors.getLiveElement(elementId));
};

function elementMatches(element) {
  const featureFlagsManager = serviceClient.get();
  const {
    ai_text_assistant_rewrite: isAiTextAssistantRewriteFeatureFlagEnabled,
    ai_text_assistant_rewrite_v2: isAiTextAssistantRewriteV2FeatureFlagEnabled,
    ai_text_assistant_write: isAiTextAssistantWriteFeatureFlagEnabled,
  } = featureFlagsManager.state;

  if (
    (!isAiTextAssistantRewriteFeatureFlagEnabled &&
      !isAiTextAssistantRewriteV2FeatureFlagEnabled) ||
    isAiTextAssistantWriteFeatureFlagEnabled
  ) {
    return false;
  }

  const parentBlock = getParentBlockByElement(element);
  const {
    textContainer,
    aiTextAssistantRestrictedBlocksSelector,
    aiTextAssistantRewriteCollapsedTextRestrictedBlocksSelector,
  } = extensions.accessors.getExtensionSettings(SLUG);

  if (
    !element.matches(textContainer) ||
    parentBlock.matches(aiTextAssistantRestrictedBlocksSelector) ||
    parentBlock.matches(aiTextAssistantRewriteCollapsedTextRestrictedBlocksSelector)
  ) {
    return false;
  }

  const typedElements = typedElementsMatches();

  return typedElements.length;
}

const control = {
  id: triggerId,
  trigger: (
    <AiWriterTrigger
      width={AI_WRITER_TRIGGER_WIDTH}
      aviabilityChecker={isAiRewriteCollapsedTextAllowed}
      onMouseDown={handleStartInteraction}
      onTouchStart={handleStartInteraction}
      onUnmount={handleUnmount}
    />
  ),
  width: AI_WRITER_TRIGGER_WIDTH,
  priority: 62,
  for: [controlTypes.COLLAPSED_TEXT],
  highlightElement: true,
  onTriggerClick: handleTriggerClick,
  matches: elementMatches,
};

export default control;
